import { Header } from '@/components/Header/Header';
import { BottomBar } from '@/components/BottomBar/BottomBar';
import { VideoButton } from '@/components/VideoButton';
import { FEEDBACK_VIDEO_URL } from './FEEDBACK_VIDEO_URL';
import { getFeedbackTagEntries, type FeedbackTag } from './feedbackTags';
import type { AttachedPhoto } from './FeedbackForm';

/**
 * Presenter Экрана 2.10.1 «Что улучшить?» — Block 10 Phase 4 (ADR-0005).
 *
 * Чистая функция от props к JSX. Никаких useEffect / fetch / useNavigate —
 * всё через Container `FeedbackForm.tsx`.
 *
 * **Layout (CLAUDE.md §16.4 + PRD §14):**
 *   1. `<VideoButton>` первым элементом контента (B-008 pattern).
 *   2. Heading "Опишите Вашу проблему".
 *   3. Textarea + character counter (min 50 / max 5000).
 *   4. 6 tag chips, **vertical** stack (P-019, ≥44px).
 *   5. customTag input — conditional на 'other' tag.
 *   6. **Photo upload section** — file input + vertical previews (max 3).
 *   7. **Screenshot opt-in section** — checkbox + optional preview.
 *   8. Error message (role="alert") при `submitState === 'error'`.
 *   9. Submit button (P-022 ≥44px, aria-busy при loading).
 *
 * **5 async states (P-021):** idle / loading / success / empty (N/A) / error.
 *
 * **P-009:** photo + screenshot — opt-in. File upload через явный `<input
 * type="file">`. Screenshot через explicit checkbox checked=true. Никакого
 * auto-capture.
 */

export type FeedbackFormSubmitState = 'idle' | 'loading' | 'error';

export interface FeedbackFormViewProps {
  // Form values
  bodyMd: string;
  tags: ReadonlySet<FeedbackTag>;
  customTag: string;
  // Async state (P-021)
  submitState: FeedbackFormSubmitState;
  errorMessage: string | null;
  // Photo + screenshot state
  attachedPhotos: readonly AttachedPhoto[];
  photoUploadError: string | null;
  screenshotEnabled: boolean;
  screenshotPreviewUrl: string | null;
  screenshotError: string | null;
  // Handlers
  onBodyMdChange: (value: string) => void;
  onTagToggle: (tag: FeedbackTag) => void;
  onCustomTagChange: (value: string) => void;
  onPhotosAdd: (files: FileList | null) => void;
  onPhotoRemove: (index: number) => void;
  onScreenshotToggle: (enabled: boolean) => void;
  onSubmit: () => void;
}

const BODY_MIN_LENGTH = 50;
const BODY_MAX_LENGTH = 5000;
const CUSTOM_TAG_MAX_LENGTH = 100;
const MAX_PHOTOS = 3;

export function FeedbackFormView(props: FeedbackFormViewProps) {
  const hasOtherTag = props.tags.has('other');
  const customTagTrimmed = props.customTag.trim();
  const canSubmit =
    props.bodyMd.length >= BODY_MIN_LENGTH &&
    props.submitState !== 'loading' &&
    (!hasOtherTag || customTagTrimmed.length > 0);
  const photosFull = props.attachedPhotos.length >= MAX_PHOTOS;

  return (
    <main className="min-h-screen flex flex-col bg-canvas">
      <Header title="Что улучшить?" />

      <section className="flex-1 p-6 overflow-y-auto flex flex-col gap-5">
        {/* VideoButton — first content per CLAUDE.md §16.4 + B-008 pattern */}
        <VideoButton videoUrl={FEEDBACK_VIDEO_URL} />

        <h1 className="font-display text-h1 text-ink m-0">Опишите Вашу проблему</h1>

        {/* Textarea + character counter */}
        <div className="flex flex-col gap-2">
          <label htmlFor="feedback-body" className="text-ink text-[16px] font-medium">
            Ваше сообщение
          </label>
          <textarea
            id="feedback-body"
            value={props.bodyMd}
            onChange={(e) => props.onBodyMdChange(e.target.value)}
            maxLength={BODY_MAX_LENGTH}
            rows={6}
            aria-describedby="feedback-body-counter"
            aria-label="Описание проблемы"
            className={[
              'w-full rounded-lg bg-card border-[1.5px] border-accent p-3 text-ink text-[16px]',
              'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none',
              'resize-y min-h-[140px]',
            ].join(' ')}
          />
          <div id="feedback-body-counter" className="text-muted text-[14px]" aria-live="off">
            {props.bodyMd.length} / {BODY_MAX_LENGTH} (минимум {BODY_MIN_LENGTH})
          </div>
        </div>

        {/* Tag chips — VERTICAL stack per P-019 */}
        <fieldset className="flex flex-col gap-2 m-0 p-0 border-0">
          <legend className="text-ink text-[16px] font-medium mb-2 p-0">Категория</legend>
          {getFeedbackTagEntries().map(([tag, label]) => {
            const checked = props.tags.has(tag);
            return (
              <button
                key={tag}
                type="button"
                role="checkbox"
                aria-checked={checked}
                aria-label={`Тег: ${label}`}
                onClick={() => props.onTagToggle(tag)}
                className={[
                  'w-full min-h-tap rounded-lg text-left px-4 py-3 text-[16px] font-medium',
                  'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
                  checked
                    ? 'bg-cta border-b-2 border-cta-shadow text-ink'
                    : 'bg-card border-[1.5px] border-accent text-ink',
                ].join(' ')}
              >
                {label}
              </button>
            );
          })}
        </fieldset>

        {/* customTag — conditional когда 'other' selected */}
        {hasOtherTag && (
          <div className="flex flex-col gap-2">
            <label htmlFor="feedback-custom-tag" className="text-ink text-[16px] font-medium">
              Уточните «Другое»
            </label>
            <input
              id="feedback-custom-tag"
              type="text"
              value={props.customTag}
              onChange={(e) => props.onCustomTagChange(e.target.value)}
              maxLength={CUSTOM_TAG_MAX_LENGTH}
              aria-label="Уточнение для тега «Другое»"
              className={[
                'w-full min-h-tap rounded-lg bg-card border-[1.5px] border-accent p-3 text-ink text-[16px]',
                'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none',
              ].join(' ')}
            />
          </div>
        )}

        {/* Photo upload section (P-019 vertical, P-009 explicit input) */}
        <div className="flex flex-col gap-3">
          <label htmlFor="feedback-photos-input" className="text-ink text-[16px] font-medium">
            Прикрепить фото (до {MAX_PHOTOS})
          </label>
          <input
            id="feedback-photos-input"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            disabled={photosFull}
            aria-label="Выбрать фото для прикрепления"
            onChange={(e) => {
              props.onPhotosAdd(e.target.files);
              // Reset input so the same file can be re-selected after remove.
              e.target.value = '';
            }}
            className="block text-[14px]"
          />
          <div className="text-muted text-[14px]" aria-live="polite">
            {props.attachedPhotos.length} / {MAX_PHOTOS}
          </div>
          {props.photoUploadError && (
            <div
              role="alert"
              aria-live="polite"
              className="rounded-lg bg-card border-[1.5px] border-error text-error text-[14px] p-3"
            >
              {props.photoUploadError}
            </div>
          )}
          {props.attachedPhotos.length > 0 && (
            <ul className="flex flex-col gap-2 list-none p-0 m-0">
              {props.attachedPhotos.map((photo, idx) => (
                <li
                  key={photo.mockKey}
                  className="flex items-center gap-3 rounded-lg bg-card border-[1.5px] border-accent p-2"
                >
                  <img
                    src={photo.previewUrl}
                    alt={`Прикреплённое фото ${idx + 1}`}
                    className="w-16 h-16 object-cover rounded"
                  />
                  <button
                    type="button"
                    onClick={() => props.onPhotoRemove(idx)}
                    aria-label={`Удалить фото ${idx + 1}`}
                    className={[
                      'ml-auto min-h-tap rounded-lg bg-card border-[1.5px] border-accent text-ink text-[14px] px-4 py-2',
                      'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
                    ].join(' ')}
                  >
                    Удалить
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Screenshot opt-in section (P-009 — explicit checkbox, no auto-capture) */}
        <div className="flex flex-col gap-3">
          <label
            htmlFor="feedback-screenshot-toggle"
            className="flex items-center gap-3 min-h-tap text-ink text-[16px] font-medium cursor-pointer"
          >
            <input
              id="feedback-screenshot-toggle"
              type="checkbox"
              checked={props.screenshotEnabled}
              onChange={(e) => props.onScreenshotToggle(e.target.checked)}
              className="w-5 h-5 focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none"
            />
            Прикрепить скриншот текущего экрана
          </label>
          {props.screenshotError && (
            <div
              role="alert"
              aria-live="polite"
              className="rounded-lg bg-card border-[1.5px] border-error text-error text-[14px] p-3"
            >
              {props.screenshotError}
            </div>
          )}
          {props.screenshotEnabled && props.screenshotPreviewUrl && (
            <img
              src={props.screenshotPreviewUrl}
              alt="Превью скриншота"
              className="w-full max-h-48 object-contain rounded-lg bg-card border-[1.5px] border-accent"
            />
          )}
        </div>

        {/* Submit error state (P-021) */}
        {props.submitState === 'error' && props.errorMessage && (
          <div
            role="alert"
            aria-live="polite"
            className="rounded-lg bg-card border-[1.5px] border-error text-error text-[14px] p-3"
          >
            {props.errorMessage}
          </div>
        )}

        {/* Submit button */}
        <button
          type="button"
          onClick={props.onSubmit}
          disabled={!canSubmit}
          aria-busy={props.submitState === 'loading'}
          className={[
            'w-full min-h-tap rounded-lg font-bold text-[18px] py-3',
            'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
            canSubmit
              ? 'bg-cta border-b-2 border-cta-shadow text-ink'
              : 'bg-card border-[1.5px] border-accent text-muted cursor-not-allowed',
          ].join(' ')}
        >
          {props.submitState === 'loading' ? 'Отправляем…' : 'Отправить'}
        </button>
      </section>

      {/* BottomBar — sticky на всех non-Home экранах (CLAUDE.md §16.4).
          onFeedback={() => {}} no-op чтобы избежать loop /feedback → /feedback. */}
      <BottomBar onFeedback={() => {}} />
    </main>
  );
}
